import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import { useState } from "react";
import { toast } from "sonner";

import { useUpdateActorVoiceStatusMutation } from "Actors/Api/Mutations/useUpdateActorVoiceStatusMutation";
import {
  type ActorVoiceListItemDto,
  ActorVoiceStatus,
  LanguageCodes,
} from "Infrastructure/Api/Api";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { EntityStatusChip } from "Shared/Components/EntityStatusChip";
import { ActorVoiceAudioPlayer } from "./ActorVoiceAudioPlayer";

interface ActorVoiceListItemProps {
  voice: ActorVoiceListItemDto;
  actorID: string;
  onEdit: () => void;
}

const LANGUAGE_LABELS: Record<string, string> = {
  [LanguageCodes.Czech]: "CZ",
  [LanguageCodes.English]: "EN",
  [LanguageCodes.Deutsch]: "DE",
};

export const ActorVoiceListItem = ({
  voice,
  actorID,
  onEdit,
}: ActorVoiceListItemProps): React.ReactNode => {
  const { t } = useResource();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const statusMutation = useUpdateActorVoiceStatusMutation();

  const isInactive = voice.status === ActorVoiceStatus.Inactive;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    onEdit();
  };

  const handleStatusChange = async () => {
    handleMenuClose();
    try {
      await statusMutation.mutateAsync({
        actorID,
        actorVoiceID: voice.actorVoiceID,
        isActive: isInactive,
      });
      toast.success(
        isInactive
          ? t(Resources.Actors.ActorDetail.VoiceActivateSuccess)
          : t(Resources.Actors.ActorDetail.VoiceDeactivateSuccess),
      );
    } catch {
      // auto-toast shown by hook
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: Spacing.XS,
        flexShrink: 0,
      }}
    >
      {isInactive && (
        <EntityStatusChip isActive={false} size="small" variant="outlined" />
      )}

      {voice.previews.map((preview) => (
        <Box
          key={preview.fileID}
          sx={{
            backgroundColor: "background.default",
            borderRadius: Radius.S,
            px: Spacing.XS,
            py: 0.25,
          }}
        >
          <ActorVoiceAudioPlayer
            fileID={preview.fileID}
            languageLabel={
              LANGUAGE_LABELS[preview.language] ?? preview.language
            }
            compact
          />
        </Box>
      ))}

      <IconButton
        size="small"
        color="inherit"
        onClick={handleMenuOpen}
        sx={{ flexShrink: 0 }}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>{t(Resources.Common.Edit)}</MenuItem>
        <MenuItem onClick={handleStatusChange}>
          {isInactive
            ? t(Resources.Actors.ActorDetail.Activate)
            : t(Resources.Actors.ActorDetail.Deactivate)}
        </MenuItem>
      </Menu>
    </Box>
  );
};
